import { createTranslation } from '@choffmeister/i18next'
import { css, SerializedStyles } from '@emotion/core'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import * as React from 'react'

import { breakpoints } from '../../constants/breakPoints'
import { useHsFormCallback } from '../../hooks/useHsFormCallback'
import { useLocalStorage } from '../../hooks/useLocalStorage'
import Block from '../Block'
import { DemoButton } from '../Button'
import { HubspotForm } from '../HubspotForm'
import Image from '../Image'
import { Link } from '../Link'
import { ButtonBlock } from './CTAMain'

interface CTABlockProps {
  bgColor?: 'blue' | 'green' | 'peach' | 'purple'
  heading?: string
  withoutSubtitle?: boolean
  subtitle?: string
  hubspotFormId?: string
  isBookADemoCTA?: boolean
  cssStyles?: SerializedStyles
  imageSrc?: {
    gatsbyImageData: IGatsbyImageData
  }
  imgAlt?: string
  imgLink?: string
  imgStyle?: SerializedStyles
}

interface ButtonBlockProps {
  blackBG?: boolean
  isBookADemoCTA?: boolean
  primaryButton?: React.ReactNode
  ghostButton?: React.ReactNode
  withoutGhostButton?: boolean
}

const CTABlock: React.FunctionComponent<CTABlockProps & ButtonBlockProps> = ({
  blackBG = false,
  isBookADemoCTA = false,
  bgColor = `${isBookADemoCTA ? 'peach' : 'purple'}`,
  heading,
  withoutSubtitle = false,
  subtitle,
  primaryButton,
  ghostButton,
  hubspotFormId,
  withoutGhostButton = false,
  cssStyles,
  imageSrc,
  imgAlt,
  imgLink,
  imgStyle,
}) => {
  const { t } = useTranslation()
  const setBlogSubscribed = useLocalStorage('blog_subscribed', false)[1]

  useHsFormCallback({
    onFormSubmittedCallback: () => setBlogSubscribed(true),
    hubspotFormIds: ['afc63de9-b452-4473-b3c3-f1f8df035204'],
  })

  return (
    <Block
      columns={2}
      className={`${bgColor}-gradient-bg with-box-shadow`}
      columnGap={0}
      noBreakForTablet
      columnsTemplate="3fr 1fr"
      css={[containerStyle(blackBG, isBookADemoCTA), !!cssStyles && cssStyles]}
    >
      <div css={textContainerStyle}>
        <h2 id="get-started-heading" className="heading2">
          {parse(heading ? heading : isBookADemoCTA ? t('bookADemo.heading') : t('tryForFree.heading'))}
        </h2>
        {!withoutSubtitle && (
          <div className="subtitle">
            {parse(subtitle ? subtitle : isBookADemoCTA ? t('bookADemo.subtitle') : t('tryForFree.subtitle'))}
          </div>
        )}
        {hubspotFormId ? (
          <div css={hubspotFormStyle}>
            <HubspotForm hubspotFormId={hubspotFormId} submitOnRight />
          </div>
        ) : primaryButton || ghostButton ? (
          <ButtonBlock
            blackBG={blackBG}
            primaryButton={primaryButton}
            ghostButton={ghostButton}
            withoutGhostButton={withoutGhostButton}
          />
        ) : (
          <DemoButton />
        )}
      </div>
      {imageSrc &&
        (imgLink ? (
          <Link href={imgLink} css={imgStyle}>
            <GatsbyImage image={imageSrc.gatsbyImageData} alt={imgAlt || heading || 'airfocus'} />
          </Link>
        ) : (
          <GatsbyImage image={imageSrc.gatsbyImageData} alt={imgAlt || heading || 'airfocus'} css={imgStyle} />
        ))}
      {!imageSrc &&
        (!isBookADemoCTA ? (
          <Image src="coins/airfocus-coin.webp" alt="airfocus coin" css={coinImageStyle} />
        ) : (
          <Image
            src="book-a-demo/book-a-demo-cta.webp"
            alt="airfocus book-a-demo consultant"
            css={demoImageStyle(isBookADemoCTA && withoutGhostButton)}
          />
        ))}
    </Block>
  )
}

export default CTABlock

const { useTranslation } = createTranslation({
  tryForFree: {
    heading: {
      en: 'Sign up for the next-gen product <br class="hide-on-small-medium-large" /> management platform now',
      fr: "Testez dès maintenant l'outil de gestion de produits nouvelle génération",
      de: 'Melde Dich jetzt für die Produkt<span class="hide-on-medium-large-desktop">-Management</span><span class="hide-on-small">management</span> Plattform der nächsten Generation an',
    },
    subtitle: {
      en: 'Join thousands of teams who use our flexible platform to build products that matter.',
      fr: "Rejoignez des milliers d'équipes qui utilisent notre plateforme flexible pour créer des produits qui comptent.",
      de: 'Schließe Dich jetzt Tausenden von Teams an, die unsere flexible Plattform nutzen, um bessere Produkte zu entwickeln.',
    },
  },
  bookADemo: {
    heading: {
      en: 'Meet with a <br class="hide-on-small-medium" /> product consultant',
      fr: 'Échangez avec un <br class="hide-on-small-medium" /> consultant produit',
      de: 'Triff einen unserer <br class="hide-on-small-medium" /> Produkt-<br class="hide-on-small hide-on-large-desktop" />Experten',
    },
    subtitle: {
      en: 'See how airfocus can fit your exact <br class="hide-on-small" /> business needs.',
      fr: `Découvrez comment airfocus peut <br class="hide-on-small" /> s'adapter sur-mesure aux besoins <br class="hide-on-small" /> de votre entreprise.`,
      de: 'Lerne, wie genau airfocus <br class="hide-on-small" /> deine Anforderungen erfüllen kann.',
    },
  },
})

const containerStyle = (blackBG: boolean, isBookADemoCTA: boolean) => css`
  min-height: 18rem;
  overflow: hidden;
  margin-top: 4rem;
  border-radius: 0.5rem;
  ${blackBG &&
  `background-image: url('/images/hero-dark.webp') !important; 
     background-position: top !important;
     background-color: rgba(255, 255, 255, 0.1);
     border: 1px solid rgba(136, 146, 158, 0.4); 
  `};
  .heading2 {
    margin-bottom: 0.75rem;
  }
  .subtitle {
    margin: 0.75rem 0 1.5rem;
    max-width: 30rem;
  }
  @media screen and (max-width: ${breakpoints.medium}px) {
    background-position-x: 85%;
    grid-template-columns: 1fr;
    margin-top: 8rem;
    min-height: auto;
    overflow: visible;
  }
  @media screen and (max-width: ${breakpoints.small}px) {
    margin-top: 4rem;
    padding: ${isBookADemoCTA ? '16rem' : '4rem'} 1rem 1.5rem;
    margin-top: 5rem;
    position: relative;
    display: block;
    > * {
      text-align: center;
    }
  }
  .hs-form {
    margin: 1rem 0 0 !important;
    .hs-input:focus,
    .hs-input:active {
      border: 2px solid #3146ff;
    }
    .actions > input {
      background: linear-gradient(to bottom, #3146ff, #3146ff);
      &:hover {
        background: linear-gradient(to bottom, #3146ffe1, #3146ff);
      }
    }
    .legal-consent-container > div > p > a {
      color: #3146ff;
    }
    @media screen and (max-width: ${breakpoints.small}px) {
      width: 100%;
      .hs-email {
        width: 100%;
        max-width: initial;
      }
    }
  }
`

const textContainerStyle = css`
  padding: 3rem 0 3rem 5rem;

  @media screen and (max-width: ${breakpoints.large}px) {
    padding: 3rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    padding: 3.5rem 3rem 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    padding: 0;
  }
`

const hubspotFormStyle = css`
  margin-top: 2rem;
`

const coinImageStyle = css`
  position: absolute !important;
  right: -4rem;
  width: 40%;

  @media screen and (max-width: ${breakpoints.large}px) {
    width: 45%;
    right: -4rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    top: -7rem;
    left: -4.69rem;
    width: 18rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    top: -4.69rem;
    width: 14.56rem;
  }
`

const demoImageStyle = (hasBookADemoButtonOnly: boolean) => css`
  position: absolute !important;
  width: 25rem;
  right: 5rem;

  @media screen and (max-width: ${breakpoints.large}px) {
    right: 3rem;
  }

  @media screen and (max-width: 860px) {
    width: 19rem;
  }

  @media screen and (max-width: ${breakpoints.medium}px) {
    width: 15rem;
    top: ${hasBookADemoButtonOnly ? '8rem' : '9rem'};
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    width: 20rem;
    top: 2.5rem;
    right: 50%;
    transform: translateX(45%);
  }
`
